import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Link } from '@sorare/core/src/routing/Link';

import { CompetitionName } from './CompetitionName';
import { GameTeamsMatchup } from './GameTeamsMatchup';
import { GameWrapper } from './GameWrapper';
import { Game_anyGameInterface } from './__generated__/index.graphql';

const SpaceBetweenHorizontal = styled(SBHorizontal)`
  padding: var(--double-unit);
  min-height: var(--triple-unit);
  min-width: 0;
`;

type Props = {
  game: Game_anyGameInterface;
  topRight?: ReactNode;
  footer?: ReactNode;
  selected?: boolean;
  to?: To;
  onClick?: () => void;
} & Pick<React.ComponentProps<typeof GameTeamsMatchup>, 'displayDay'> &
  Pick<
    React.ComponentProps<typeof GameWrapper>,
    'labelProps' | 'withLightning'
  >;

export const Game = ({
  game,
  topRight,
  footer,
  labelProps,
  withLightning,
  selected,
  to,
  onClick,
  displayDay,
}: Props) => {
  const matchup = <GameTeamsMatchup game={game} displayDay={displayDay} />;

  const wrapper = (
    <GameWrapper
      className={classNames({ selected })}
      withLightning={withLightning}
      labelProps={labelProps}
    >
      <SpaceBetweenHorizontal gap={1}>
        <CompetitionName displayName={game.competition.displayName} />
        {topRight}
      </SpaceBetweenHorizontal>
      {to ? (
        <LinkOverlay as={Link} to={to}>
          {matchup}
        </LinkOverlay>
      ) : (
        matchup
      )}
      {footer}
    </GameWrapper>
  );

  if (to) {
    return <LinkBox>{wrapper}</LinkBox>;
  }

  return <ButtonBase onClick={onClick}>{wrapper}</ButtonBase>;
};

Game.fragments = {
  anyGameInterface: gql`
    fragment Game_anyGameInterface on AnyGameInterface {
      id
      status: statusTyped
      competition {
        slug
        displayName
      }
      ...GameTeamsMatchup_anyGameInterface
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
  ` as TypedDocumentNode<Game_anyGameInterface>,
};
